import { Helmet } from "react-helmet-async";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../Utils/Axios";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

function AdminFieldDragDrop() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [expenseFields, setExpenseFields] = useState([
    { fieldName: 'Vehicle', fieldNameUrdu: 'گاڑی', fieldType: 'number', isRequired: false, order: 0 },
    { fieldName: 'Cash', fieldNameUrdu: 'نقدی', fieldType: 'number', isRequired: false, order: 1 },
    { fieldName: 'Rent', fieldNameUrdu: 'کرایہ', fieldType: 'number', isRequired: false, order: 2 },
    { fieldName: 'Commission', fieldNameUrdu: 'کمیشن', fieldType: 'number', isRequired: false, order: 3 },
    { fieldName: 'Labor', fieldNameUrdu: 'مزدوری', fieldType: 'number', isRequired: false, order: 4 },
  ]);
  const [salesFields, setSalesFields] = useState([
    { fieldName: 'Raw Sale', fieldNameUrdu: 'خام بِکری', fieldType: 'number', isRequired: true, order: 0 },
    { fieldName: 'Expenses', fieldNameUrdu: 'اخراجات', fieldType: 'number', isRequired: false, order: 1 },
    { fieldName: 'Net Sale', fieldNameUrdu: 'پختہ بِکری', fieldType: 'number', isRequired: false, order: 2 }
  ]);
  const [isLoading, setIsLoading] = useState(false);

  const getToken = () => localStorage.getItem("adminToken");

  // Memoize ReactQuill modules to prevent re-renders
  const quillModules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['clean']
    ]
  }), []);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      toast.error("Please login as admin first");
      navigate("/admin/login");
      return;
    }

    fetchCompanies();
  }, [navigate]);

  const fetchCompanies = async () => {
    try {
      const token = getToken();
      const response = await api.get("/admin/companies", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data.companies || []);
    } catch (error) {
      console.error("Error fetching companies:", error);
      toast.error("Failed to fetch companies");
    }
  };

  const handleCompanySelect = async (companyId) => {
    if (!companyId) {
      setSelectedCompany(null);
      return;
    }

    try {
      const token = getToken();
      const response = await api.get(`/admin/companies/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const company = response.data.company;
      setSelectedCompany(company);

      // Fetch template for this company
      const templateResponse = await api.get(`/bill/template/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (templateResponse.data.template) {
        const template = templateResponse.data.template;
        if (template.expenseFields && template.expenseFields.length > 0) {
          setExpenseFields(template.expenseFields);
        }
        if (template.salesFields && template.salesFields.length > 0) {
          setSalesFields(template.salesFields);
        }
      }
    } catch (error) {
      console.error("Error fetching company:", error);
      toast.error("Failed to fetch company details");
    }
  };

  const handleAddExpenseField = () => {
    setExpenseFields([...expenseFields, {
      fieldName: '',
      fieldNameUrdu: '',
      fieldType: 'number',
      isRequired: false,
      order: expenseFields.length
    }]);
  };

  const handleRemoveExpenseField = (index) => {
    setExpenseFields(expenseFields.filter((_, i) => i !== index));
  };

  const handleAddSalesField = () => {
    setSalesFields([...salesFields, {
      fieldName: '',
      fieldNameUrdu: '',
      fieldType: 'number',
      isRequired: false,
      order: salesFields.length
    }]);
  };

  const handleRemoveSalesField = (index) => {
    setSalesFields(salesFields.filter((_, i) => i !== index));
  };

  // Handle drag end for expense fields
  const onExpenseFieldsDragEnd = (result) => {
    if (!result.destination) return;
    
    const items = Array.from(expenseFields);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    // Update order property
    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index
    }));
    
    setExpenseFields(updatedItems);
  };

  // Handle drag end for sales fields
  const onSalesFieldsDragEnd = (result) => {
    if (!result.destination) return;
    
    const items = Array.from(salesFields);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    // Update order property
    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index
    }));
    
    setSalesFields(updatedItems);
  };

  // Handler for expense field name changes with rich text
  const handleExpenseFieldNameChange = useCallback((index, value) => {
    setExpenseFields(prev => {
      const updated = [...prev];
      if (updated[index].fieldName !== value) {
        updated[index] = { ...updated[index], fieldName: value };
        return updated;
      }
      return prev;
    });
  }, []);

  // Handler for expense field name Urdu changes with rich text
  const handleExpenseFieldNameUrduChange = useCallback((index, value) => {
    setExpenseFields(prev => {
      const updated = [...prev];
      if (updated[index].fieldNameUrdu !== value) {
        updated[index] = { ...updated[index], fieldNameUrdu: value };
        return updated;
      }
      return prev;
    });
  }, []);

  // Handler for sales field name changes with rich text
  const handleSalesFieldNameChange = useCallback((index, value) => {
    setSalesFields(prev => {
      const updated = [...prev];
      if (updated[index].fieldName !== value) {
        updated[index] = { ...updated[index], fieldName: value };
        return updated;
      }
      return prev;
    });
  }, []);

  // Handler for sales field name Urdu changes with rich text
  const handleSalesFieldNameUrduChange = useCallback((index, value) => {
    setSalesFields(prev => {
      const updated = [...prev];
      if (updated[index].fieldNameUrdu !== value) {
        updated[index] = { ...updated[index], fieldNameUrdu: value };
        return updated;
      }
      return prev;
    });
  }, []);

  const handleSaveFields = async () => {
    if (!selectedCompany) {
      toast.error("Please select a company first");
      return;
    }

    setIsLoading(true);
    try {
      const token = getToken();
      await api.post(`/bill/template/${selectedCompany._id}`, {
        expenseFields,
        salesFields
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Fields saved successfully!");
    } catch (error) {
      console.error("Error saving fields:", error);
      toast.error("Failed to save fields");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Field Management - Drag & Drop</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Field Management</h1>
                <p className="text-gray-600">Drag and drop fields to reorder them</p>
              </div>
              <button
                onClick={() => navigate("/admin/bills")}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg"
              >
                ← Back to Bill Management
              </button>
            </div>
          </div>

          {/* Company Selection */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Company
            </label>
            <select
              value={selectedCompany?._id || ''}
              onChange={(e) => handleCompanySelect(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
            >
              <option value="">-- Select Company --</option>
              {companies.map((company) => (
                <option key={company._id} value={company._id}>
                  {company.username} - {company.email}
                </option>
              ))}
            </select>
          </div>

          {selectedCompany && (
            <>
              {/* Expense Fields */}
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-semibold text-gray-900">Expense Fields</h2>
                  <button
                    onClick={handleAddExpenseField}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm"
                  >
                    + Add Field
                  </button>
                </div>
                <DragDropContext onDragEnd={onExpenseFieldsDragEnd}>
                  <Droppable droppableId="expenseFields">
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-3">
                        {expenseFields.map((field, index) => (
                          <Draggable key={index} draggableId={`expense-${index}`} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`flex gap-3 items-start p-4 bg-gray-50 rounded-lg border-2 transition-all ${
                                  snapshot.isDragging 
                                    ? 'shadow-xl border-indigo-500 bg-indigo-50' 
                                    : 'border-transparent hover:border-gray-300'
                                }`}
                              >
                                <div
                                  {...provided.dragHandleProps}
                                  className="flex items-center justify-center cursor-grab active:cursor-grabbing text-gray-400 hover:text-indigo-600 p-2 rounded"
                                  title="Drag to reorder"
                                >
                                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                                  </svg>
                                </div>
                                <div className="flex-1">
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Field Name (English)
                                  </label>
                                  <ReactQuill
                                    theme="snow"
                                    value={field.fieldName || ''}
                                    onChange={(value) => handleExpenseFieldNameChange(index, value)}
                                    placeholder="Field Name (English)"
                                    modules={quillModules}
                                    style={{ minHeight: '80px', marginBottom: '10px' }}
                                  />
                                  <label className="block text-xs font-medium text-gray-600 mb-1 mt-2">
                                    Field Name (Urdu)
                                  </label>
                                  <ReactQuill
                                    theme="snow"
                                    value={field.fieldNameUrdu || ''}
                                    onChange={(value) => handleExpenseFieldNameUrduChange(index, value)}
                                    placeholder="Field Name (Urdu)"
                                    modules={quillModules}
                                    style={{ minHeight: '80px' }}
                                  />
                                </div>
                                <button
                                  onClick={() => handleRemoveExpenseField(index)}
                                  className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg text-sm self-start mt-8"
                                >
                                  Remove
                                </button>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>

              {/* Sales Fields */}
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-semibold text-gray-900">Sales Fields</h2>
                  <button
                    onClick={handleAddSalesField}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm"
                  >
                    + Add Field
                  </button>
                </div>
                <DragDropContext onDragEnd={onSalesFieldsDragEnd}>
                  <Droppable droppableId="salesFields">
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-3">
                        {salesFields.map((field, index) => (
                          <Draggable key={index} draggableId={`sales-${index}`} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`flex gap-3 items-start p-4 bg-gray-50 rounded-lg border-2 transition-all ${
                                  snapshot.isDragging 
                                    ? 'shadow-xl border-indigo-500 bg-indigo-50' 
                                    : 'border-transparent hover:border-gray-300'
                                }`}
                              >
                                <div
                                  {...provided.dragHandleProps}
                                  className="flex items-center justify-center cursor-grab active:cursor-grabbing text-gray-400 hover:text-indigo-600 p-2 rounded"
                                  title="Drag to reorder"
                                >
                                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                                  </svg>
                                </div>
                                <div className="flex-1">
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Field Name (English)
                                  </label>
                                  <ReactQuill
                                    theme="snow"
                                    value={field.fieldName || ''}
                                    onChange={(value) => handleSalesFieldNameChange(index, value)}
                                    placeholder="Field Name (English)"
                                    modules={quillModules}
                                    style={{ minHeight: '80px', marginBottom: '10px' }}
                                  />
                                  <label className="block text-xs font-medium text-gray-600 mb-1 mt-2">
                                    Field Name (Urdu)
                                  </label>
                                  <ReactQuill
                                    theme="snow"
                                    value={field.fieldNameUrdu || ''}
                                    onChange={(value) => handleSalesFieldNameUrduChange(index, value)}
                                    placeholder="Field Name (Urdu)"
                                    modules={quillModules}
                                    style={{ minHeight: '80px' }}
                                  />
                                </div>
                                <button
                                  onClick={() => handleRemoveSalesField(index)}
                                  className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg text-sm self-start mt-8"
                                >
                                  Remove
                                </button>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>

              {/* Save Button */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <button
                  onClick={handleSaveFields}
                  disabled={isLoading}
                  className="w-full bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Saving...' : 'Save Fields'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default AdminFieldDragDrop;

